import { useEffect, useRef } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { generateWaveformBuffer, hasRespiratorySwing } from '../../engine/waveformEngine';
import styles from './WaveformCanvas.module.css';

const SAMPLE_RATE = 250; // Hz
const DISPLAY_DURATION = 10; // seconds

// Literal hex colors — canvas context cannot use CSS variables.
// ECG green, Pleth (SpO₂) white, CO₂ yellow, Art red, CVP teal.
const WAVEFORM_COLORS = {
  ecg: '#00E676',
  spo2: '#FFFFFF',
  capno: '#FFD600',
  abp: '#FF1744',
  cvp: '#26C6DA',
};

// Never let a waveform-buffer generation throw propagate — returns null on
// failure so callers keep the last good buffer instead of crashing the tree.
function safeGenerateBuffer(...args) {
  try {
    return generateWaveformBuffer(...args);
  } catch (e) {
    console.error('waveform buffer generation failed:', e);
    return null;
  }
}

export default function WaveformCanvas({
  waveformType,
  bufferDuration = 6,       // seconds of pre-computed samples; capno uses 12
  samplesPerFrame: spfProp, // optional per-channel scan speed override
}) {
  const canvasRef = useRef(null);
  const bufferRef = useRef(null);
  const writeHeadRef = useRef(0);
  const animationIdRef = useRef(null);
  // Store CSS pixel dimensions separately from the DPR-scaled canvas backing store
  const cssDimsRef = useRef({ width: 0, height: 0 });
  // Keep bufferDuration in a ref so the draw closure always sees the latest value
  const bufferDurationRef = useRef(bufferDuration);
  bufferDurationRef.current = bufferDuration;

  const vitals = useSimStore((state) => state.vitals);
  const rhythm = useSimStore((state) => state.rhythm);
  const prevRhythm = useSimStore((state) => state.prevRhythm);
  const rhythmProgress = useSimStore((state) => state.rhythmProgress);
  const capnoMorphology = useSimStore((state) => state.capnoMorphology);
  // Severe hypovolaemia / haemorrhage (and tamponade, handled by rhythm) drive a
  // respiratory swing in pulse amplitude on the pleth + arterial trace.
  const respSwing = useSimStore((state) => hasRespiratorySwing(state.activeModifier, state.activeScenario));
  // Instructor-set Pulse Pressure Variation → arterial-line swing depth (0 = off).
  const ppvDepth = useSimStore((state) =>
    state.ppv?.enabled ? Math.max(0.05, Math.min(0.6, (state.ppv.percent ?? 0) / 100)) : 0);
  const vitalsRef = useRef(vitals);
  vitalsRef.current = vitals;
  const respSwingRef = useRef(respSwing);
  respSwingRef.current = respSwing;
  const ppvDepthRef = useRef(ppvDepth);
  ppvDepthRef.current = ppvDepth;
  const rhythmRef = useRef(rhythm);
  rhythmRef.current = rhythm;
  const prevRhythmRef = useRef(prevRhythm);
  prevRhythmRef.current = prevRhythm;
  const rhythmProgressRef = useRef(rhythmProgress);
  rhythmProgressRef.current = rhythmProgress;
  const capnoMorphologyRef = useRef(capnoMorphology);
  capnoMorphologyRef.current = capnoMorphology;

  // Initial buffer generation. Guarded: a throw here (in a useEffect) would
  // unmount the tree without an error boundary — keep the last good buffer instead.
  useEffect(() => {
    const b = safeGenerateBuffer(waveformType, vitalsRef.current, SAMPLE_RATE, bufferDuration, rhythm, capnoMorphology, prevRhythmRef.current, rhythmProgressRef.current, respSwingRef.current, ppvDepthRef.current);
    if (b) bufferRef.current = b;
  }, [waveformType, bufferDuration, rhythm, capnoMorphology, respSwing, ppvDepth]);

  // Handle canvas sizing with ResizeObserver so it stays correct as layout changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      // Read the CSS layout size from the element's current rendered dimensions.
      // Do NOT set canvas.style.width/height — that would pin the size and prevent
      // flex from redistributing space when sibling rows are toggled on/off.
      // CSS (flex: 1) owns the layout size; we only set the backing-store resolution.
      const cssWidth = canvas.getBoundingClientRect().width;
      const cssHeight = canvas.getBoundingClientRect().height;
      if (cssWidth === 0 || cssHeight === 0) return;

      cssDimsRef.current = { width: cssWidth, height: cssHeight };
      // Setting canvas.width/height resets the context transform, so re-apply DPR scale.
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const color = WAVEFORM_COLORS[waveformType] || '#ffffff';
    // Per-channel scan speed. Default: 6s buffer sweeps in 12s (0.5× real-time).
    // Capno override: 12s buffer + slower spf gives ~30s sweep = ~2.5 breaths visible.
    const samplesPerFrame = spfProp ?? SAMPLE_RATE / 120;

    let lastHR = null;
    let lastRR = null;
    let lastRhythm = null;
    let lastPrevRhythm = null;
    let lastCapnoMorphology = null;
    let lastProgressQuant = null;
    let lastRespSwing = null;
    let lastPpvDepth = null;

    const draw = () => {
      const { width: cssW, height: cssH } = cssDimsRef.current;
      if (cssW === 0 || cssH === 0) {
        animationIdRef.current = requestAnimationFrame(draw);
        return;
      }

      const v = vitalsRef.current;
      const currentRhythm = rhythmRef.current;
      const currentPrevRhythm = prevRhythmRef.current;
      const currentCapnoMorphology = capnoMorphologyRef.current;
      const currentRespSwing = respSwingRef.current;
      const currentPpvDepth = ppvDepthRef.current;
      const hr = Math.round(v.HR?.current || 0);
      const rr = Math.round(v.RR?.current || 0);
      // Quantise the rhythm cross-fade so we regenerate ~40 times across the ramp
      // (smooth) rather than on every fractional store update.
      const progressQuant = Math.round(rhythmProgressRef.current * 40);

      // Regenerate buffer on rate, rhythm (from/to), capnogram, resp-swing, PPV, or cross-fade change
      if (hr !== lastHR || rr !== lastRR || currentRhythm !== lastRhythm || currentPrevRhythm !== lastPrevRhythm || currentCapnoMorphology !== lastCapnoMorphology || progressQuant !== lastProgressQuant || currentRespSwing !== lastRespSwing || currentPpvDepth !== lastPpvDepth) {
        const b = safeGenerateBuffer(waveformType, v, SAMPLE_RATE, bufferDurationRef.current, currentRhythm, currentCapnoMorphology, currentPrevRhythm, rhythmProgressRef.current, currentRespSwing, currentPpvDepth);
        if (b) bufferRef.current = b;   // keep last good buffer if generation fails
        lastHR = hr;
        lastRR = rr;
        lastRhythm = currentRhythm;
        lastPrevRhythm = currentPrevRhythm;
        lastCapnoMorphology = currentCapnoMorphology;
        lastProgressQuant = progressQuant;
        lastRespSwing = currentRespSwing;
        lastPpvDepth = currentPpvDepth;
      }

      const buffer = bufferRef.current;
      if (!buffer) {
        animationIdRef.current = requestAnimationFrame(draw);
        return;
      }

      // Clear canvas
      ctx.fillStyle = '#111118';
      ctx.fillRect(0, 0, cssW, cssH);

      // Grid
      drawGrid(ctx, cssW, cssH);

      // Sweep position from a SHARED wall-clock so every lane with the same sweep
      // period stays in phase — a single synchronized erase bar across ECG / pleth
      // / ABP / CVP, regardless of when each canvas mounted (e.g. an art line
      // toggled on mid-case). Preserves the prior speed: N samples cross the canvas
      // in N/(samplesPerFrame·60) seconds.
      const N = buffer.length;
      const sweepPeriodMs = (N / (samplesPerFrame * 60)) * 1000;
      const writeHead = ((Date.now() % sweepPeriodMs) / sweepPeriodMs) * N;
      drawWaveform(ctx, cssW, cssH, buffer, writeHead, color);

      animationIdRef.current = requestAnimationFrame(draw);
    };

    animationIdRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationIdRef.current);
  }, [waveformType]);

  return <canvas ref={canvasRef} className={styles.waveformCanvas} />;
}

function drawGrid(ctx, cssW, cssH) {
  const spacing = 20;
  ctx.strokeStyle = 'rgba(100, 100, 150, 0.15)';
  ctx.lineWidth = 0.5;

  for (let x = 0; x < cssW; x += spacing) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, cssH);
    ctx.stroke();
  }
  for (let y = 0; y < cssH; y += spacing) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(cssW, y);
    ctx.stroke();
  }
}

/**
 * Sweeping write-head rendering (GE Carescape style):
 * Buffer index k maps to a fixed x = (k / N) * cssW.
 * The cursor sweeps right as writeHead advances.
 * A blank "eraser zone" ahead of the cursor separates fresh from stale data.
 */
function drawWaveform(ctx, cssW, cssH, buffer, writeHead, color) {
  const N = buffer.length;
  const centerY = cssH / 2;
  const blankWidth = 18; // px

  // Cursor x position (CSS pixels)
  const cursorX = (writeHead / N) * cssW;

  // Draw waveform path, skipping the blank zone
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();

  let pathStarted = false;

  for (let k = 0; k < N; k++) {
    const x = (k / N) * cssW;

    // Distance ahead of cursor (wrap-around)
    const dist = (x - cursorX + cssW) % cssW;
    if (dist < blankWidth) {
      pathStarted = false;
      continue;
    }

    const sample = buffer[k];
    const y = centerY - sample * (centerY * 0.78);

    if (!pathStarted) {
      ctx.moveTo(x, y);
      pathStarted = true;
    } else {
      ctx.lineTo(x, y);
    }
  }

  ctx.stroke();

  // Paint blank eraser zone
  ctx.fillStyle = '#111118';
  const eraseX = (cursorX + 1 + cssW) % cssW;
  if (eraseX + blankWidth <= cssW) {
    ctx.fillRect(eraseX, 0, blankWidth, cssH);
  } else {
    ctx.fillRect(eraseX, 0, cssW - eraseX, cssH);
    ctx.fillRect(0, 0, blankWidth - (cssW - eraseX), cssH);
  }

  // Cursor tick
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cursorX, 0);
  ctx.lineTo(cursorX, cssH);
  ctx.stroke();
}
